import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Download, Send, CheckCircle2, Clock, Mail, FileText, PenTool, Loader2 } from 'lucide-react';

const QuoteView = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [prospect, setProspect] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendToEmail, setSendToEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [terms, setTerms] = useState('');
  const [sendingContract, setSendingContract] = useState(null);

  useEffect(() => {
    if (quoteId) {
      fetchQuote();
    }
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      console.log('Fetching quote:', quoteId);
      const response = await api.get(`/quotes/${quoteId}`);
      console.log('Quote response:', response.data);
      setQuote(response.data.quote);
      
      // Try to fetch as prospect first, if that fails, try as client
      const prospectId = response.data.quote.prospect_id;
      console.log('Trying to fetch prospect/client:', prospectId);
      
      // Initialize with fallback data
      let prospectData = {
        id: prospectId,
        company_name: 'Unknown Company',
        contact_name: '',
        contact_email: '',
        address: ''
      };
      
      try {
        const prospectRes = await api.get(`/prospects/${prospectId}`);
        console.log('Found as prospect:', prospectRes.data);
        prospectData = prospectRes.data;
      } catch (prospectError) {
        console.log('Not found as prospect, trying client...', prospectError.response?.status);
        // If prospect not found, try to fetch as client
        try {
          const clientRes = await api.get(`/clients/${prospectId}`);
          console.log('Found as client:', clientRes.data);
          prospectData = {
            id: clientRes.data.id,
            company_name: clientRes.data.company_name,
            contact_name: clientRes.data.contact_name || clientRes.data.company_name || '',
            contact_email: clientRes.data.contact_email || clientRes.data.email || '',
            address: clientRes.data.address || ''
          };
        } catch (clientError) {
          console.error('Could not find prospect or client:', clientError.response?.status);
          // Keep the fallback data initialized above
        }
      }
      
      console.log('Final prospect data:', prospectData);
      setProspect(prospectData);
      
      // Set default email from prospect/client or from quote's sent_to_email
      const emailToUse = (prospectData && prospectData.contact_email) || 
                        (response.data.quote && response.data.quote.sent_to_email) || 
                        '';
      console.log('Setting email to:', emailToUse);
      setSendToEmail(emailToUse);
    } catch (error) {
      console.error('Quote fetch error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load quote: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await api.get(`/quotes/${quoteId}/contracts`);
      setContracts(response.data.contracts || []);
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await api.get('/settings/terms-and-conditions');
      setTerms(response.data.terms || '');
    } catch (error) {
      console.error('Failed to fetch terms:', error);
    }
  };

  const handleGenerateContract = async () => {
    setGeneratingContract(true);
    try {
      const response = await api.post(`/quotes/${quoteId}/generate-contract`);
      
      if (response.data.hardware_only) {
        // Show terms & conditions for hardware-only quotes
        await fetchTerms();
        setShowTerms(true);
        toast.info('Please review and accept the Terms & Conditions');
      } else {
        // Generate signing tokens for each contract
        const generatedContracts = response.data.contracts || [];
        
        for (const contract of generatedContracts) {
          try {
            await api.post(`/contracts/${contract.contract_id}/send-for-signature`, {
              email: prospect?.contact_email || prospect?.email,
              name: prospect?.contact_name || prospect?.company_name
            });
          } catch (error) {
            console.error('Failed to create signing token:', error);
          }
        }
        
        toast.success(`${generatedContracts.length} contract(s) generated successfully!`);
        await fetchContracts();
      }
    } catch (error) {
      console.error('Failed to generate contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate contract');
    } finally {
      setGeneratingContract(false);
    }
  };

  const handleAcceptTerms = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

  const handleSendContract = async (contractId) => {
    setSendingContract(contractId);
    try {
      await api.post(`/contracts/${contractId}/send-for-signature`, {
        email: prospect?.contact_email || prospect?.email,
        name: prospect?.contact_name || prospect?.company_name
      });
      
      toast.success('Contract sent successfully!');
      await fetchContracts(); // Refresh to get updated contract with signing token
    } catch (error) {
      console.error('Failed to send contract:', error);
      toast.error('Failed to send contract');
    } finally {
      setSendingContract(null);
    }
  };


    try {
      await api.post(`/quotes/${quoteId}/accept-terms`);
      toast.success('Terms accepted successfully!');
      setShowTerms(false);
      fetchQuote(); // Refresh quote to show terms_accepted status
    } catch (error) {
      console.error('Failed to accept terms:', error);
      toast.error('Failed to accept terms');
    }
  };

  // Fetch contracts when quote is loaded, and auto-generate if needed
  useEffect(() => {
    if (quote) {
      fetchContracts();
      // Auto-generate contracts if quote is accepted and no contracts exist
      if (quote.status === 'accepted') {
        autoGenerateContractsIfNeeded();
      }
    }
  }, [quote]);

  const autoGenerateContractsIfNeeded = async () => {
    try {
      const response = await api.get(`/quotes/${quoteId}/contracts`);
      const existingContracts = response.data.contracts || [];
      
      // Only auto-generate if no contracts exist yet
      if (existingContracts.length === 0 && !quote.terms_accepted) {
        await handleGenerateContract();
      }
    } catch (error) {
      console.error('Error checking contracts:', error);
    }
  };


  const handleSendQuote = async () => {
    if (!sendToEmail || !sendToEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSending(true);
    try {
      const response = await api.post(`/quotes/${quoteId}/send`, {
        email_override: sendToEmail
      });
      toast.success(`Quote sent successfully to ${sendToEmail}!`);
      setShowEmailInput(false);
      fetchQuote(); // Refresh to update status
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send quote';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <p className="text-slate-500">Quote not found</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-slate-100 text-slate-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <Clock className="w-4 h-4" />;
      case 'sent': return <Send className="w-4 h-4" />;
      case 'accepted': return <CheckCircle2 className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/prospects')}
                className="border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Quote {quote.quote_number}</h1>
                <p className="text-sm text-slate-500">{prospect?.company_name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(quote.status)}`}>
                {getStatusIcon(quote.status)}
                <span className="ml-2">{quote.status.replace('_', ' ')}</span>
              </span>
            </div>
          </div>
          
          {/* Email Input Section */}
          {quote.status === 'draft' && (
            <div className="mt-4 flex items-end gap-3">
              <div className="flex-1">
                <Label htmlFor="send_to_email" className="text-sm font-medium text-slate-700 mb-1 block">
                  Send Quote To
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="send_to_email"
                    type="email"
                    value={sendToEmail}
                    onChange={(e) => setSendToEmail(e.target.value)}
                    placeholder="prospect@example.com"
                    className="pl-10"
                  />
                </div>
              </div>
              <Button
                onClick={handleSendQuote}
                disabled={sending || !sendToEmail}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send Quote'}
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Quote Details Card */}
        <Card className="shadow-sm border-slate-200 mb-6">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-blue-50">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl">Quote {quote.quote_number}</CardTitle>
                <CardDescription className="mt-2">
                  Created on {new Date(quote.created_at).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </CardDescription>
              </div>
              {quote.valid_until && (
                <div className="text-right">
                  <p className="text-sm text-slate-600">Valid Until</p>
                  <p className="font-semibold text-slate-800">
                    {new Date(quote.valid_until).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Customer Info */}
            <div className="mb-8">
              <h3 className="font-semibold text-slate-800 mb-3">Customer Information</h3>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-1">Company Name</p>
                  <p className="text-lg font-semibold text-slate-800">{prospect?.company_name}</p>
                </div>
                {prospect?.contact_name && (
                  <p className="text-sm text-slate-700 mb-1">
                    <span className="font-medium">Contact:</span> {prospect.contact_name}
                  </p>
                )}
                {prospect?.contact_email && (
                  <p className="text-sm text-slate-700 mb-1">
                    <span className="font-medium">Email:</span> {prospect.contact_email}
                  </p>
                )}
                {prospect?.address && (
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">Address:</span> {prospect.address}
                  </p>
                )}
              </div>
            </div>

            {/* Services */}
            <div className="mb-8">
              <h3 className="font-semibold text-slate-800 mb-3">Services</h3>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Service</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Price/User</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Quantity</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.services.map((item, index) => {
                      const basePrice = item.base_price || 0;
                      const quantity = item.quantity || 0;
                      const total = item.total !== undefined ? item.total : (basePrice * quantity);
                      
                      return (
                        <tr key={index} className="border-b border-slate-100 last:border-b-0">
                          <td className="py-3 px-4 text-slate-800">{item.service_name || item.name || 'Unnamed Service'}</td>
                          <td className="py-3 px-4 text-right text-slate-600">
                            £{basePrice.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-center text-slate-600">
                            {quantity}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-slate-800">
                            £{total.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-slate-50 rounded-lg p-6">
              <div className="space-y-3">
                <div className="flex justify-between text-slate-700">
                  <span>Subtotal</span>
                  <span className="font-medium">£{(quote.subtotal || 0).toFixed(2)}</span>
                </div>
                
                {(quote.discount_percent || 0) > 0 && (
                  <div className="flex justify-between text-slate-700">
                    <span>Discount ({quote.discount_percent}%)</span>
                    <span className="font-medium">-£{(quote.discount_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                
                <div className="pt-3 border-t border-slate-300 flex justify-between text-lg font-semibold">
                  <span className="text-slate-800">Total per Month</span>
                  <span className="text-emerald-600">£{(quote.total || 0).toFixed(2)}</span>
                </div>
                
                <div className="text-right text-sm text-slate-600">
                  £{((quote.total || 0) * 12).toFixed(2)} per year
                </div>
              </div>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className="mt-6">
                <h3 className="font-semibold text-slate-800 mb-2">Notes</h3>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{quote.notes}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>


        {/* Contract Actions - Show when quote is accepted */}
        {quote.status === 'accepted' && (
          <Card className="shadow-sm border-emerald-200 bg-emerald-50/50 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Contract & Agreement
              </CardTitle>
              <CardDescription>
                Review and sign the service contract to proceed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {contracts.length === 0 && !quote.terms_accepted ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    A contract needs to be generated for this quote before services can begin.
                  </p>
                  <Button
                    onClick={handleGenerateContract}
                    disabled={generatingContract}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {generatingContract ? 'Generating...' : 'Generate Contract'}
                  </Button>
                </div>
              ) : contracts.length > 0 ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    {contracts.map((contract, index) => (
                      <div key={contract.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-slate-800">{contract.title}</p>
                            <p className="text-sm text-slate-500">
                              Status: <span className={`font-medium ${contract.status === 'signed' ? 'text-green-600' : 'text-amber-600'}`}>
                                {contract.status === 'signed' ? 'Signed' : 'Pending Signature'}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {contract.status !== 'signed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendContract(contract.id)}
                              disabled={sendingContract === contract.id}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                            >
                              {sendingContract === contract.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-1" />
                                  Send
                                </>
                              )}
                            </Button>
                          )}
                          {contract.signing_token && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`/contracts/sign/${contract.signing_token}`, '_blank')}
                              className="border-slate-300 text-slate-600 hover:bg-slate-50"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          )}
                          {contract.status !== 'signed' && contract.signing_token && (
                            <Button
                              size="sm"
                              onClick={() => window.open(`/contracts/sign/${contract.signing_token}`, '_blank')}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <PenTool className="w-4 h-4 mr-1" />
                              Sign Contract
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {contracts.some(c => c.status !== 'signed') && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        <strong>⚠️ Action Required:</strong> Please review and sign all contracts before services go live. You will receive email reminders.
                      </p>
                    </div>
                  )}
                </div>
              ) : quote.terms_accepted ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-800">
                      <strong>Hardware Purchase Terms Accepted</strong>
                    </p>
                  </div>
                  <p className="text-sm text-green-700 mt-2">
                    Accepted on {new Date(quote.terms_accepted_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Terms & Conditions Modal for Hardware-Only Quotes */}
        {showTerms && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-800">Hardware Purchase Terms & Conditions</h2>
                <p className="text-sm text-slate-600 mt-1">Please review and accept to proceed</p>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: terms }}
                />
              </div>
              
              <div className="p-6 border-t border-slate-200">
                <div className="flex items-center space-x-3 mb-4">
                  <input
                    type="checkbox"
                    id="accept-terms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                  />
                  <label htmlFor="accept-terms" className="text-sm text-slate-700">
                    I have read and accept the Terms & Conditions
                  </label>
                </div>
                
                <div className="flex items-center justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowTerms(false);
                      setTermsAccepted(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAcceptTerms}
                    disabled={!termsAccepted}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Accept & Continue
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Timeline */}
        {(quote.sent_at || quote.viewed_at || quote.accepted_at) && (
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Quote Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2" />
                  <div>
                    <p className="font-medium text-slate-800">Quote Created</p>
                    <p className="text-sm text-slate-600">
                      {new Date(quote.created_at).toLocaleString('en-GB')}
                    </p>
                  </div>
                </div>
                
                {quote.sent_at && (
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">Quote Sent</p>
                      <p className="text-sm text-slate-600">
                        {new Date(quote.sent_at).toLocaleString('en-GB')}
                      </p>
                      {quote.sent_to_email && (
                        <p className="text-sm text-blue-600 mt-1 flex items-center">
                          <Mail className="w-3 h-3 mr-1" />
                          {quote.sent_to_email}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {quote.viewed_at && (
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2" />
                    <div>
                      <p className="font-medium text-slate-800">Quote Viewed</p>
                      <p className="text-sm text-slate-600">
                        {new Date(quote.viewed_at).toLocaleString('en-GB')}
                      </p>
                    </div>
                  </div>
                )}
                
                {quote.accepted_at && (
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2" />
                    <div>
                      <p className="font-medium text-slate-800">Quote Accepted</p>
                      <p className="text-sm text-slate-600">
                        {new Date(quote.accepted_at).toLocaleString('en-GB')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default QuoteView;
